/**
 * ComplianceChecklist
 *
 * P6 — Procedural / declarative items the extractor correctly excludes from
 * the substantive question list (sign declarations, complete forms, format
 * rules, "Disclose conflicts of interest", "Acknowledge data-controller"
 * etc.). Surfaced here so the bidder still sees them and can tick each off
 * as their team handles it.
 *
 * Local-only checked-state via localStorage (UI-state convenience). No
 * backend persistence yet — designing for future SME-routing P4 will move
 * this into MongoDB so multiple team members share the same checklist.
 */

import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { CheckCircle2, Circle, FileSignature, ShieldAlert, ClipboardList } from 'lucide-react';
import { theme } from '../styles/theme';
import { ComplianceItem } from '../types';

const Container = styled.section`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  overflow: hidden;
`;

const Header = styled.header`
  background: linear-gradient(135deg, ${theme.colors.warning[100]}80, ${theme.colors.primary[50]});
  padding: ${theme.spacing[5]};
  border-bottom: 1px solid ${theme.colors.border};
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
`;

const HeaderIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: ${theme.colors.warning[500]};
  color: white;
  border-radius: ${theme.borderRadius.md};
  flex-shrink: 0;
`;

const HeaderText = styled.div`
  flex: 1;
  h3 {
    margin: 0 0 ${theme.spacing[1]} 0;
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.semibold};
    font-size: ${theme.typography.fontSize.lg};
  }
  p {
    margin: 0;
    color: ${theme.colors.textSecondary};
    font-size: ${theme.typography.fontSize.sm};
    line-height: ${theme.typography.lineHeight.relaxed};
  }
`;

const ProgressBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
`;

const ItemList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Item = styled.li<{ $checked: boolean }>`
  display: grid;
  grid-template-columns: 32px 1fr;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  border-bottom: 1px solid ${theme.colors.border};
  cursor: pointer;
  transition: background ${theme.transitions.base};
  opacity: ${({ $checked }) => ($checked ? 0.55 : 1)};
  &:hover { background: ${theme.colors.surfaceHover}; }
  &:last-child { border-bottom: none; }
`;

const CheckIconWrap = styled.button<{ $checked: boolean }>`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  color: ${({ $checked }) => ($checked ? theme.colors.success[500] : theme.colors.textMuted)};
  margin-top: 2px;
  &:hover { color: ${theme.colors.success[600]}; }
`;

const ItemBody = styled.div<{ $checked: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
  text-decoration: ${({ $checked }) => ($checked ? 'line-through' : 'none')};
`;

const ItemText = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textPrimary};
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const ItemMeta = styled.span`
  display: inline-flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[3]};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
`;

const Tag = styled.span<{ $tone?: 'warning' | 'info' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: ${theme.borderRadius.full};
  font-weight: ${theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  ${({ $tone }) =>
    $tone === 'warning'
      ? `background:${theme.colors.warning[100]};color:${theme.colors.warning[700]};`
      : $tone === 'info'
      ? `background:${theme.colors.primary[100]};color:${theme.colors.primary[700]};`
      : `background:${theme.colors.backgroundSecondary};color:${theme.colors.textMuted};`}
`;

const Empty = styled.div`
  padding: ${theme.spacing[6]};
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
`;

const KIND_TO_ICON: Record<string, React.ReactNode> = {
  declaration_or_eligibility: <FileSignature size={14} />,
  procedural: <ClipboardList size={14} />,
  ambiguous: <ShieldAlert size={14} />,
};

const KIND_TO_LABEL: Record<string, string> = {
  declaration_or_eligibility: 'Declaration / Eligibility',
  procedural: 'Procedural / Format',
  ambiguous: 'Review needed',
};

const KIND_TO_TONE: Record<string, 'warning' | 'info' | 'neutral'> = {
  declaration_or_eligibility: 'warning',
  procedural: 'info',
  ambiguous: 'neutral',
};

interface Props {
  items: ComplianceItem[];
  /** Per-thread storage key so multiple analyses don't share state. */
  storageKey?: string;
}

const ComplianceChecklist: React.FC<Props> = ({ items, storageKey }) => {
  const key = storageKey || 'opentender_compliance_checks';
  const [checked, setChecked] = useState<Set<string>>(new Set());

  // Stable identifier for an item: prefer id, fall back to content hash-like string.
  const idOf = (item: ComplianceItem, idx: number) =>
    item.id || `${(item.section || 'no-section').slice(0, 30)}|${(item.text || '').slice(0, 80)}|${idx}`;

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw) setChecked(new Set(JSON.parse(raw)));
    } catch {
      /* localStorage unavailable — silently no-op */
    }
  }, [key]);

  const persist = (next: Set<string>) => {
    setChecked(next);
    try {
      window.localStorage.setItem(key, JSON.stringify(Array.from(next)));
    } catch {
      /* ignore */
    }
  };

  const toggle = (id: string) => {
    const next = new Set(checked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    persist(next);
  };

  const completed = useMemo(
    () => items.filter((it, i) => checked.has(idOf(it, i))).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items, checked]
  );

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <Container>
      <Header>
        <HeaderIcon>
          <ClipboardList size={20} />
        </HeaderIcon>
        <HeaderText>
          <h3>Compliance Checklist</h3>
          <p>
            Procedural and declarative items extracted from the source document.
            These aren't substantive prose-response questions but the bidder still
            needs to address each one before submission (sign declarations, complete
            forms, acknowledge clauses, comply with format rules).
          </p>
        </HeaderText>
        <ProgressBadge>
          {completed} / {items.length} addressed
        </ProgressBadge>
      </Header>

      <ItemList>
        {items.map((item, idx) => {
          const id = idOf(item, idx);
          const isChecked = checked.has(id);
          const kind = item.compliance_kind || 'ambiguous';
          return (
            <Item key={id} $checked={isChecked} onClick={() => toggle(id)}>
              <CheckIconWrap
                $checked={isChecked}
                onClick={(e) => {
                  e.stopPropagation();
                  toggle(id);
                }}
                aria-label={isChecked ? 'Mark as outstanding' : 'Mark as addressed'}
              >
                {isChecked ? <CheckCircle2 size={20} /> : <Circle size={20} />}
              </CheckIconWrap>
              <ItemBody $checked={isChecked}>
                <ItemText>{item.text}</ItemText>
                <ItemMeta>
                  {item.section && <span>📍 {item.section}</span>}
                  {item.type && <span>{item.type}</span>}
                  <Tag $tone={KIND_TO_TONE[kind] || 'neutral'}>
                    {KIND_TO_ICON[kind] || <ShieldAlert size={14} />}
                    <span style={{ marginLeft: 4 }}>
                      {KIND_TO_LABEL[kind] || 'Compliance item'}
                    </span>
                  </Tag>
                  {item.compliance_reason && (
                    <span style={{ color: theme.colors.textMuted }} title={item.compliance_reason}>
                      ⓘ
                    </span>
                  )}
                </ItemMeta>
              </ItemBody>
            </Item>
          );
        })}
      </ItemList>
      {items.length === 0 && <Empty>All compliance items have been resolved.</Empty>}
    </Container>
  );
};

export default ComplianceChecklist;
